/* eslint react-hooks/exhaustive-deps: "warn" */
import { useEffect, useRef } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';
import { useMediaQuery } from 'hooks/useMediaQuery';

import { Flag } from 'ui/atoms/Flag/Flag';
import { Icon } from 'ui/atoms/Icon/Icon';
import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './CrmDataEnrichmentCodes.module.scss';

type Translate = (key: string) => string;

type Scheme = {
  name: string;
  source: string;
  flags: string[];
  applies: string;
  depth: string;
  status: string;
  amber?: boolean;
  changing: string;
};

const getSchemes = (t: Translate): Scheme[] => [
  {
    name: 'NACE Rev. 2.1',
    source: 'Eurostat',
    flags: ['eu'],
    applies: t('EU and EEA'),
    depth: t('Section, division, group, class · 4 digits'),
    status: t('Since 1 Jan 2025'),
    changing: t(
      'Replaced NACE Rev. 2 under Delegated Regulation (EU) 2023/137. Transmission phases in by statistical domain through 2031, so both versions circulate.',
    ),
  },
  {
    name: 'UK SIC 2007',
    source: 'ONS · Companies House',
    flags: ['gb'],
    applies: t('United Kingdom'),
    depth: t('5-digit sub-class'),
    status: 'SIC 2007',
    changing: t(
      'ONS published UK SIC 2026 on 28 April 2026, with 668 classes across 22 sections. Companies House has not set a mandatory adoption date, so filings still carry SIC 2007.',
    ),
  },
  {
    name: 'NAICS 2022',
    source: 'Census · StatCan · INEGI',
    flags: ['us', 'ca', 'mx'],
    applies: t('United States, Canada, Mexico'),
    depth: t('6 digits'),
    status: 'NAICS 2022',
    changing: t(
      'The 2027 revision is out for public comment. OMB publishes the updated structure during 2026 and NAICS 2027 becomes available in January 2027.',
    ),
  },
  {
    name: 'WZ 2025',
    source: 'Statistisches Bundesamt',
    flags: ['de'],
    applies: t('Germany'),
    depth: t('5-digit sub-class'),
    status: t('Since 1 Jan 2025'),
    changing: t(
      '983 sub-classes against 839 under WZ 2008, so a single old code can split several ways. Both editions run in parallel while statistical areas migrate.',
    ),
  },
  {
    name: 'ATECO 2025',
    source: 'Istat',
    flags: ['it'],
    applies: t('Italy'),
    depth: t('National extension of NACE'),
    status: t('Since 1 Jan 2025'),
    changing: t(
      'Operational in the Registro Imprese since 1 April 2025, replacing ATECO 2007. Chambers of commerce reassigned codes automatically using the Istat conversion table.',
    ),
  },
  {
    name: 'NAF rev. 2',
    source: 'Insee · code APE',
    flags: ['fr'],
    applies: t('France'),
    depth: t('5 characters, e.g. 62.01Z'),
    status: 'NAF rev. 2',
    amber: true,
    changing: t(
      'NAF 2025 was approved by decree in July 2025 and applies from 1 January 2027. Through 2026 the Sirene register shows both the current APE code and the future one.',
    ),
  },
  {
    name: 'ISIC Rev. 5',
    source: 'UN Statistics Division',
    flags: [],
    applies: t('Global reference'),
    depth: t('4 digits'),
    status: 'Rev. 5',
    changing: t(
      'Endorsed in 2023. Countries adapted their national classifications and business registers across 2025 and 2026 and implement from 2027, which is why every other row on this table is moving. It is also what lets a German WZ code and a US NAICS code sit in the same report without being restated by hand.',
    ),
  },
];

const MOBILE_QUERY_WIDTH = 1024;

const SchemeFlags = ({ flags }: { flags: string[] }) => {
  if (!flags.length) {
    return <Icon type="globe-1" className={styles.globeIcon} />;
  }

  return (
    <span className={styles.flagRow}>
      {flags.map((code) => (
        <Flag key={code} code={code} className={styles.flagIcon} />
      ))}
    </span>
  );
};

export const CrmDataEnrichmentCodes = () => {
  const { t } = useInt();

  const isDesktop = useMediaQuery(MOBILE_QUERY_WIDTH);

  const SCHEMES = getSchemes(t);

  const headRef = useRef<HTMLDivElement>(null);
  const rowRefs = useRef<(HTMLElement | null)[]>([]);

  useEffect(() => {
    const revealables = [headRef.current, ...rowRefs.current].filter((el): el is HTMLElement => Boolean(el));

    if (!revealables.length) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add(styles.revealed);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 },
    );

    revealables.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, [isDesktop]);

  return (
    <Section background="#EBF2FB" className={styles.root}>
      <div ref={headRef} className={cn(styles.head, styles.reveal)}>
        <TitleBlock align="left" title={t('Industry codes are local, and nearly all of them are mid-revision.')} />
      </div>

      {isDesktop === false ? (
        <div className={styles.cards}>
          {SCHEMES.map((scheme, i) => (
            <div
              key={scheme.name}
              ref={(el) => {
                rowRefs.current[i] = el;
              }}
              className={cn(styles.card, styles.reveal)}
              style={{ transitionDelay: `${i * 60}ms` }}
            >
              <div className={styles.cardHead}>
                <div>
                  <h3 className={styles.cardTitle}>{scheme.name}</h3>
                  <span className={styles.cardSource}>{scheme.source}</span>
                </div>
                <SchemeFlags flags={scheme.flags} />
              </div>

              <div className={styles.cardRow}>
                <span className={styles.cardLabel}>{t('Applies to')}</span>
                <span className={styles.cardValue}>{scheme.applies}</span>
              </div>
              <div className={styles.cardRow}>
                <span className={styles.cardLabel}>{t('Depth')}</span>
                <span className={styles.cardValue}>{scheme.depth}</span>
              </div>
              <div className={styles.cardRow}>
                <span className={styles.cardLabel}>{t('In force now')}</span>
                <span className={cn(styles.status, scheme.amber && styles.statusAmber)}>{scheme.status}</span>
              </div>
              <p className={styles.cardChanging}>{scheme.changing}</p>
            </div>
          ))}
        </div>
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>{t('Scheme')}</th>
                <th>{t('Applies to')}</th>
                <th>{t('Depth')}</th>
                <th>{t('In force now')}</th>
                <th>{t('What is changing')}</th>
              </tr>
            </thead>
            <tbody>
              {SCHEMES.map((scheme, i) => (
                <tr
                  key={scheme.name}
                  ref={(el) => {
                    rowRefs.current[i] = el;
                  }}
                  className={cn(styles.row, styles.reveal)}
                  style={{ transitionDelay: `${i * 60}ms` }}
                >
                  <td className={styles.schemeCell}>
                    <span className={styles.schemeName}>{scheme.name}</span>
                    <span className={styles.schemeSource}>{scheme.source}</span>
                  </td>
                  <td className={styles.appliesCell}>
                    <SchemeFlags flags={scheme.flags} />
                    {scheme.applies}
                  </td>
                  <td className={styles.depthCell}>{scheme.depth}</td>
                  <td>
                    <span className={cn(styles.status, scheme.amber && styles.statusAmber)}>{scheme.status}</span>
                  </td>
                  <td className={styles.changingCell}>{scheme.changing}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
};
